import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import api from "../../api";
import { hasAdminAccess } from "../../utils/roleAccess";

export default function GoogleCalender() {
  const brand = useMemo(
    () => ({
      teal: "#55c5d0",
      tealHover: "#48b7c3",
      border: "rgb(229,231,235)",
      bg: "rgb(255,255,255)",
    }),
    []
  );

  const location = useLocation();
  
  // Get user info from Redux store to check role
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.type || user?.role;

  const [loading, setLoading] = useState(false);
  const [apiMsg, setApiMsg] = useState(null);
  const [connected, setConnected] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // if completed=true, we fetch ALL events (no date param) once
  const [completedMode, setCompletedMode] = useState(false);

  // viewMonth = first day of the month being shown in the grid
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(null); // yyyy-mm-dd or null
  const [events, setEvents] = useState([]); // normalized list
  const [listLoading, setListLoading] = useState(false);

  // Use the configured API instance instead of manual axios calls
  // This ensures proper CORS handling and authentication

  const extractAuthUrl = (data) =>
    data?.authorization_url ||
    data?.auth_url ||
    data?.url ||
    data?.redirect_url ||
    null;

  // -------- helpers ----------
  const ymdLocal = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
  };

  // 12-hour lowercase, drop :00 minutes (e.g., 6pm, 7:30am)
  const timeLabel12 = (iso) => {
    try {
      const d = new Date(iso);
      let h = d.getHours();
      const m = d.getMinutes();
      const ampm = h >= 12 ? "pm" : "am";
      h = h % 12;
      if (h === 0) h = 12;
      if (m === 0) return `${h}${ampm}`;
      return `${h}:${String(m).padStart(2, "0")}${ampm}`;
    } catch {
      return iso || "—";
    }
  };

  // normalize different response shapes to an array
  const normalizeResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.events)) return data.events;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  };

  // --- Connection + completed detection ---
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qpConnected = params.get("connected") === "1";
    const qpCompleted =
      params.get("completed") === "true" || params.get("completed") === "1";
    const flag = sessionStorage.getItem("google_calendar_connected") === "1";

    if (qpCompleted) {
      setConnected(true);
      setCompletedMode(true);
      sessionStorage.removeItem("google_calendar_connected");
      return;
    }

    if (qpConnected || flag) {
      setConnected(true);
      sessionStorage.removeItem("google_calendar_connected");
      return;
    }

    (async () => {
      try {
        const res = await api.get(`google/calendar/status/`, {
          timeout: 10000,
        });
        if (res?.data?.connected === true) setConnected(true);
      } catch {
        /* ignore */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // When connected, fetch all events once (month grid filters locally)
  useEffect(() => {
    if (!connected) return;
    fetchAllEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  const handleGoogleOauthStart = async () => {
    setLoading(true);
    setApiMsg(null);
    try {
      const callbackUrl = `${window.location.origin}/google-calender/callback`;
      const statePayload = btoa(
        JSON.stringify({ redirect: "/google-calender" })
      );

      const res = await api.get(`google/calendar/oauth/start/`, {
        timeout: 30000,
        params: {
          callback_url: callbackUrl,
          state: statePayload,
        },
      });

      const authUrl = extractAuthUrl(res?.data ?? {});
      if (authUrl) {
        window.location.href = authUrl;
        return;
      }
      setApiMsg({ type: "info", data: res?.data ?? {} });
    } catch (err) {
      const data =
        err?.response?.data ?? { detail: "Failed to start Google OAuth." };
      setApiMsg({ type: "error", data });
    } finally {
      setLoading(false);
    }
  };

  //   disconnect api
  const handleDisconnect = async () => {
    const ok = window.confirm("Disconnect Google Calendar?");
    if (!ok) return;

    try {
      setDisconnecting(true);
      setApiMsg(null);

      await api.post(`google/calendar/disconnect/`, {}, { timeout: 20000 });

      // clear flags and UI state
      sessionStorage.removeItem("google_calendar_connected");
      setConnected(false);
      setCompletedMode(false);
      setEvents([]);

      // remove query params like ?connected=1&completed=1
      const cleanUrl = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, "", cleanUrl);

      setApiMsg({
        type: "info",
        data: { detail: "Disconnected from Google Calendar." },
      });
    } catch (err) {
      setApiMsg({
        type: "error",
        data: err?.response?.data ?? { detail: "Failed to disconnect." },
      });
    } finally {
      setDisconnecting(false);
    }
  };

  // ---------- Fetchers ----------
  const fetchAllEvents = async () => {
    try {
      setListLoading(true);
      setApiMsg(null);

      const res = await api.get(`google/calendar/events/`, {
        timeout: 30000,
      });

      const raw = normalizeResponse(res?.data);

      const normalized = raw
        .map((e, idx) => {
          const id = e.id ?? e.event_id ?? String(idx);
          const startISO = e.start || e.start_time || e.startDateTime;
          const endISO = e.end || e.end_time || e.endDateTime;
          return {
            id,
            title: e.title || e.summary || "Untitled Event",
            startISO,
            endISO,
            dateKey: startISO ? ymdLocal(startISO) : "Unknown",
            location: e.location || "",
            status: e.status || "",
            htmlLink: e.htmlLink,
            attendees: Array.isArray(e.attendees) ? e.attendees : [],
          };
        })
        .sort((a, b) => new Date(a.startISO) - new Date(b.startISO));

      setEvents(normalized);
    } catch (err) {
      setEvents([]);
      setApiMsg({
        type: "error",
        data:
          err?.response?.data ?? {
            detail: "Failed to load events.",
          },
      });
    } finally {
      setListLoading(false);
    }
  };

  // Group events by yyyy-mm-dd for fast lookup in the month grid
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const key = ev.dateKey || (ev.startISO ? ymdLocal(ev.startISO) : null);
      if (!key) continue;
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  // Build the 6x7 (or shorter) grid of dates for viewMonth
  const monthGrid = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startWeekday = firstOfMonth.getDay(); // 0 = Sun
    const gridStart = new Date(year, month, 1 - startWeekday);

    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      cells.push({
        date: d,
        key: `${d.getFullYear()}-${mm}-${dd}`,
        inMonth: d.getMonth() === month,
      });
    }
    // Trim trailing all-out-of-month week
    while (cells.length > 35 && !cells.slice(-7).some((c) => c.inMonth)) {
      cells.splice(-7, 7);
    }
    return cells;
  }, [viewMonth]);

  const todayKey = useMemo(() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
  }, []);

  const monthLabel = viewMonth.toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  });

  const goPrevMonth = () =>
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const goNextMonth = () =>
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  const goToday = () => {
    const d = new Date();
    setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
  };

  // ---------------------------

  // small, reusable spinner for buttons
  const Spinner = ({ className = "h-4 w-4" }) => (
    <svg
      className={`animate-spin ${className}`}
      viewBox="0 0 24 24"
      role="status"
      aria-label="Loading"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4A4 4 0 008 12H4z"
      />
    </svg>
  );

  // UI helpers
  const StatusBadge = ({ status }) => {
    if (!status) return null;
    const map =
      status === "confirmed"
        ? "bg-green-100 text-green-700 border-green-200"
        : status === "tentative"
        ? "bg-yellow-100 text-yellow-800 border-yellow-200"
        : "bg-gray-100 text-gray-700 border-gray-200";
    return (
      <span className={`text-xs px-2 py-0.5 rounded border ${map}`}>
        {status}
      </span>
    );
  };

  const Attendees = ({ attendees }) => {
    if (!Array.isArray(attendees) || attendees.length === 0) return null;
    return (
      <div className="mt-1 flex flex-wrap gap-1">
        {attendees.map((a, i) => {
          const email =
            typeof a === "string" ? a : a?.email || a?.displayName || "Attendee";
          return (
            <span
              key={`${email}-${i}`}
              className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700"
              title={email}
            >
              {email}
            </span>
          );
        })}
      </div>
    );
  };

  // Check if user has permission to access Google Calendar
  if (!user || !(userRole === 'agent' || userRole === 'admin' || hasAdminAccess(user))) {
    return (
      <div className="min-h-screen" style={{ background: brand.bg }}>
        <main className="mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-screen-lg">
          <div className="bg-white rounded-xl shadow-sm border" style={{ borderColor: brand.border }}>
            <div className="p-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-red-500 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Access Denied
              </h2>
              <p className="text-slate-600 mb-6">
                Only agents and administrators can access the Google Calendar integration.
                {!user && " Please log in to continue."}
              </p>
              {!user && (
                <button
                  onClick={() => window.location.href = '/login'}
                  className="inline-flex items-center px-6 py-3 rounded-lg text-white font-medium shadow-sm hover:shadow-md transition-all"
                  style={{ background: brand.teal }}
                >
                  Go to Login
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: brand.bg }}>
      <main
        className={`mx-auto px-4 sm:px-6 lg:px-8 py-8 ${
          connected ? "max-w-screen-2xl" : "max-w-screen-lg"
        }`}
      >
        {/* NOT CONNECTED -> Centered modal */}
        {!connected ? (
          <div className="min-h-[70vh] grid place-items-center">
            <div
              className="w-full max-w-md bg-white rounded-2xl shadow-xl border p-8 text-center"
              style={{ borderColor: brand.border }}
            >
              <div
                className="mx-auto mb-4 h-12 w-12 rounded-full flex items-center justify-center border"
                style={{ borderColor: brand.border }}
              >
                {/* calendar-like icon */}
                <svg viewBox="0 0 24 24" className="h-6 w-6 text-slate-700">
                  <path
                    fill="currentColor"
                    d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a2 2 0 0 1 2 2v3H3V6a2 2 0 0 1 2-2h1V3a1 1 0 1 1 2 0v1zm14 7v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9zM7 13h4v4H7z"
                  />
                </svg>
              </div>

              <h1 className="text-xl sm:text-2xl font-semibold text-slate-900">
                Connect Google Calendar
              </h1>
              <p className="text-slate-600 text-sm mt-2">
                Link your calendar to view your events here.
              </p>

              <button
                onClick={handleGoogleOauthStart}
                disabled={loading}
                className={`mt-6 inline-flex items-center justify-center gap-2 w-full px-5 py-2.5 rounded-lg text-white font-medium shadow-sm ${
                  loading ? "cursor-not-allowed opacity-90" : "hover:shadow"
                }`}
                style={{
                  backgroundColor: loading ? brand.tealHover : brand.teal,
                }}
                title="Connect Google Calendar"
                aria-busy={loading}
                aria-live="polite"
              >
                {loading ? (
                  <>
                    <Spinner className="h-4 w-4 text-white" />
                    <span>Connecting…</span>
                  </>
                ) : (
                  <>Connect</>
                )}
              </button>

              {apiMsg && (
                <div className="mt-4 text-left">
                  {apiMsg.type === "error" ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-800 text-sm">
                      <p className="font-medium mb-1">Couldn’t connect.</p>
                      <pre className="whitespace-pre-wrap break-words text-xs">
                        {JSON.stringify(apiMsg.data, null, 2)}
                      </pre>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-800 text-sm">
                      <p className="font-medium mb-1">Response</p>
                      <pre className="whitespace-pre-wrap break-words text-xs">
                        {JSON.stringify(apiMsg.data, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          // CONNECTED -> Wider page like others
          <div
            className="bg-white rounded-2xl shadow-sm border p-6 sm:p-8"
            style={{ borderColor: brand.border }}
          >
            {/* Header with Disconnect on the right */}
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">
                  Google Calendar
                </h1>
                <p className="text-slate-600 text-sm mt-1">
                  {`${events.length} event${events.length === 1 ? "" : "s"} synced`}
                </p>
              </div>

              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-slate-700 hover:bg-slate-50 ${
                  disconnecting ? "opacity-70 cursor-not-allowed" : ""
                }`}
                style={{ borderColor: brand.border }}
                title="Disconnect Google Calendar"
                aria-busy={disconnecting}
                aria-live="polite"
              >
                {disconnecting ? (
                  <>
                    <Spinner />
                    <span>Disconnecting…</span>
                  </>
                ) : (
                  <>Disconnect</>
                )}
              </button>
            </div>

            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={goPrevMonth}
                  className="px-3 py-1.5 rounded-lg border text-slate-700 hover:bg-slate-50"
                  style={{ borderColor: brand.border }}
                  aria-label="Previous month"
                >
                  ‹
                </button>
                <button
                  onClick={goNextMonth}
                  className="px-3 py-1.5 rounded-lg border text-slate-700 hover:bg-slate-50"
                  style={{ borderColor: brand.border }}
                  aria-label="Next month"
                >
                  ›
                </button>
                <button
                  onClick={goToday}
                  className="px-3 py-1.5 rounded-lg border text-slate-700 hover:bg-slate-50 text-sm"
                  style={{ borderColor: brand.border }}
                >
                  Today
                </button>
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                {monthLabel}
              </h2>
              <div className="w-[180px]" />
            </div>

            {/* Month grid */}
            <div
              className="rounded-xl border overflow-hidden"
              style={{ borderColor: brand.border }}
            >
              {/* Weekday header */}
              <div
                className="grid grid-cols-7 bg-slate-50 border-b text-xs font-medium text-slate-600"
                style={{ borderColor: brand.border }}
              >
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                  <div key={d} className="px-2 py-2 text-center">
                    {d}
                  </div>
                ))}
              </div>

              {/* Cells */}
              {listLoading ? (
                <div className="p-6 text-sm text-slate-600">Loading events…</div>
              ) : (
                <div className="grid grid-cols-7">
                  {monthGrid.map((cell) => {
                    const dayEvents = eventsByDay[cell.key] || [];
                    const isToday = cell.key === todayKey;
                    const isSelected = cell.key === selectedDay;
                    return (
                      <button
                        type="button"
                        key={cell.key}
                        onClick={() =>
                          setSelectedDay((s) => (s === cell.key ? null : cell.key))
                        }
                        className={`text-left min-h-[110px] border-b border-r p-1.5 align-top transition-colors ${
                          cell.inMonth ? "bg-white" : "bg-slate-50/60"
                        } ${isSelected ? "ring-2 ring-inset" : "hover:bg-slate-50"}`}
                        style={{
                          borderColor: brand.border,
                          ...(isSelected ? { boxShadow: `inset 0 0 0 2px ${brand.teal}` } : {}),
                        }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span
                            className={`inline-flex items-center justify-center text-xs font-medium h-6 w-6 rounded-full ${
                              isToday ? "text-white" : cell.inMonth ? "text-slate-800" : "text-slate-400"
                            }`}
                            style={isToday ? { background: brand.teal } : {}}
                          >
                            {cell.date.getDate()}
                          </span>
                          {dayEvents.length > 0 && (
                            <span className="text-[10px] text-slate-500">
                              {dayEvents.length}
                            </span>
                          )}
                        </div>
                        <div className="space-y-1">
                          {dayEvents.slice(0, 3).map((ev) => (
                            <div
                              key={ev.id}
                              title={`${ev.title} • ${timeLabel12(ev.startISO)}`}
                              className="truncate text-[11px] leading-tight rounded px-1.5 py-0.5 text-white"
                              style={{ background: brand.teal }}
                            >
                              <span className="opacity-90 mr-1">
                                {timeLabel12(ev.startISO)}
                              </span>
                              {ev.title}
                            </div>
                          ))}
                          {dayEvents.length > 3 && (
                            <div className="text-[10px] text-slate-500 px-1">
                              +{dayEvents.length - 3} more
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Selected day detail */}
            {selectedDay && (
              <div
                className="mt-5 rounded-xl border"
                style={{ borderColor: brand.border }}
              >
                <div
                  className="px-4 py-3 border-b flex items-center justify-between"
                  style={{ borderColor: brand.border }}
                >
                  <h3 className="font-medium text-slate-900">
                    Events on {selectedDay} ({(eventsByDay[selectedDay] || []).length})
                  </h3>
                  <button
                    onClick={() => setSelectedDay(null)}
                    className="text-xs text-slate-500 hover:text-slate-700"
                  >
                    Close
                  </button>
                </div>
                <div className="p-4">
                  {(eventsByDay[selectedDay] || []).length === 0 ? (
                    <div className="text-sm text-slate-500">No events.</div>
                  ) : (
                    <ul className="space-y-3">
                      {(eventsByDay[selectedDay] || []).map((ev) => (
                        <li
                          key={ev.id}
                          className="rounded-lg border p-3"
                          style={{ borderColor: brand.border }}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium text-slate-900 truncate">
                              {ev.title}
                            </div>
                            <StatusBadge status={ev.status} />
                          </div>
                          <div className="text-sm text-slate-600 mt-0.5">
                            {timeLabel12(ev.startISO)} → {timeLabel12(ev.endISO)}
                          </div>
                          {ev.location && (
                            <div className="text-sm text-slate-600">
                              {ev.location}
                            </div>
                          )}
                          <Attendees attendees={ev.attendees} />
                          {ev.htmlLink && (
                            <a
                              href={ev.htmlLink}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-[#55c5d0] underline block mt-1"
                            >
                              Open in Google Calendar
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {/* API message after actions */}
            {apiMsg && (
              <div className="mt-4">
                {apiMsg.type === "error" ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-800 text-sm">
                    <p className="font-medium mb-1">Request failed</p>
                    <pre className="whitespace-pre-wrap break-words text-xs">
                      {JSON.stringify(apiMsg.data, null, 2)}
                    </pre>
                  </div>
                ) : (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-800 text-sm">
                    <p className="font-medium mb-1">Response</p>
                    <pre className="whitespace-pre-wrap break-words text-xs">
                      {JSON.stringify(apiMsg.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
